import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  InventorySupplierPaymentFilterParams,
  InventorySupplierPaymentRecord,
  RecordSupplierPaymentRequest,
} from '../types/inventory';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

const mapPayment = (
  row: Record<string, unknown>,
): InventorySupplierPaymentRecord => ({
  id: String(row.id || ''),
  company_id: toNumber(row.company_id),
  outlet_id: String(row.outlet_id || ''),
  supplier_id: String(row.supplier_id || ''),
  payment_date: String(row.payment_date || ''),
  amount: toNumber(row.amount),
  payment_method_id:
    row.payment_method_id === null ? null : toNumber(row.payment_method_id),
  reference: (row.reference as string | null) ?? null,
  notes: (row.notes as string | null) ?? null,
  journal_entry_id: (row.journal_entry_id as string | null) ?? null,
  created_by: (row.created_by as string | null) ?? null,
  created_at: String(row.created_at || ''),
  updated_at: String(row.updated_at || ''),
});

export const fetchSupplierPayments = async ({
  companyId,
  outletId,
  supplierId = 'all',
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: InventorySupplierPaymentFilterParams): Promise<
  ApiResponse<PaginatedResponse<InventorySupplierPaymentRecord>>
> => {
  try {
    let query = supabase
      .from('supplier_payments')
      .select('*')
      .eq('company_id', companyId)
      .eq('outlet_id', outletId)
      .order('payment_date', { ascending: false })
      .order('created_at', { ascending: false });

    if (supplierId && supplierId !== 'all') {
      query = query.eq('supplier_id', supplierId);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const allRows = (data || []).map((row) =>
      mapPayment(row as Record<string, unknown>),
    );
    const totalItems = allRows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const from = (page - 1) * pageSize;
    const pagedRows = allRows.slice(from, from + pageSize);

    return {
      success: true,
      data: {
        data: pagedRows,
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch supplier payments',
      data: undefined,
    };
  }
};

export const recordSupplierPayment = async (
  request: RecordSupplierPaymentRequest,
): Promise<
  ApiResponse<{ paymentId: string; journalEntryId: string | null }>
> => {
  try {
    const allocations = request.allocations.map((a) => ({
      invoice_id: a.invoiceId,
      amount_applied: a.amountApplied,
    }));

    const { data, error } = await supabase.rpc('record_supplier_payment', {
      p_company_id: request.companyId,
      p_outlet_id: request.outletId,
      p_supplier_id: request.supplierId,
      p_payment_date: request.paymentDate,
      p_amount: request.amount,
      p_payment_method_id: request.paymentMethodId ?? null,
      p_reference: request.reference ?? null,
      p_notes: request.notes ?? null,
      p_allocations: allocations,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const result = data as {
      success: boolean;
      error?: string;
      payment_id?: string;
      journal_entry_id?: string;
    };

    if (!result.success) {
      return {
        success: false,
        error: result.error ?? 'Failed to record payment',
        data: undefined,
      };
    }

    return {
      success: true,
      data: {
        paymentId: result.payment_id ?? '',
        journalEntryId: result.journal_entry_id ?? null,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to record payment',
      data: undefined,
    };
  }
};
